import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPercent } from '../../Utils/decimalUtils';
import WithTooltip from '../Fundamentals/Tooltip/WithTooltip';
import BilledImpactLine from './BilledImpactLine';
import ExcludedTieredPlansNote from './ExcludedTieredPlansNote';
import { calculateImpactBreakdown } from './impactBreakdown';
import type { ImpactRow, PackageImpact } from './impactBreakdown';
import type { Calculations } from './PriceSimulationWorksheet.types';
import WorksheetTotal from './WorksheetTotal';
import { deltaClass, formatSignedSek, orEmpty, withSek } from './worksheetFormatters';

interface AnalysisTabContentProps {
    calculations: Calculations;
}

const SignedImpactCell = ({ amount, hasQuantity }: { amount: number; hasQuantity: boolean }) => (
    <td className={`text-right ${deltaClass(hasQuantity ? amount : 0)}`}>{orEmpty(hasQuantity, formatSignedSek(amount))}</td>
);

const ImpactTableRow = ({ row }: { row: ImpactRow }) => {
    const { t } = useTranslation();
    const isTier = row.level === 'tier';

    return (
        <tr className={isTier ? 'semibold' : undefined}>
            <td className={isTier ? undefined : 'pl-24 text-secondary'}>{row.months === null ? row.label : t('priceSimulation.bindingTimeMonths', { count: row.months })}</td>
            {row.hasAnnualValue ? (
                <>
                    <td className="text-right">{orEmpty(row.hasQuantity, withSek(row.currentAnnualValue))}</td>
                    <td className="text-right">{orEmpty(row.hasQuantity, withSek(row.newAnnualValue))}</td>
                    <SignedImpactCell amount={row.impact} hasQuantity={row.hasQuantity} />
                    <SignedImpactCell amount={row.billedImpact} hasQuantity={row.hasQuantity} />
                    <td className="text-right">{orEmpty(row.hasQuantity, formatPercent(row.weightedIncrease))}</td>
                </>
            ) : (
                <td colSpan={5} className="text-secondary">
                    {t('priceSimulation.noAnnualValue')}
                </td>
            )}
        </tr>
    );
};

const PackageImpactCard = ({ pkg }: { pkg: PackageImpact }) => {
    const { t } = useTranslation();

    return (
        <div className="bg-default border rounded-8 overflow-hidden">
            <div className="d-flex justify-content-between align-items-center px-24 py-16 border-bottom">
                <h3 className="heading-md m-0">{pkg.packageName}</h3>
                {pkg.hasAnnualValue ? (
                    <div className="d-flex flex-column align-items-end">
                        <WorksheetTotal
                            label={t('priceSimulation.annualValue')}
                            original={pkg.currentAnnualValue}
                            next={pkg.newAnnualValue}
                            hasQuantity={pkg.hasQuantity}
                            changeClassName="ml-4"
                        />
                        <BilledImpactLine amount={pkg.billedImpact} hasQuantity={pkg.hasQuantity} className="align-self-end" textClassName="body-sm" />
                    </div>
                ) : (
                    <span className="body-sm text-secondary">{t('priceSimulation.noAnnualValue')}</span>
                )}
            </div>
            <div className="table-responsive">
                <table className="table table-plain table-relaxed worksheet-table mb-0">
                    <thead>
                        <tr>
                            <th />
                            <th className="text-right">{t('priceSimulation.currentAnnualValue')}</th>
                            <th className="text-right">{t('priceSimulation.newAnnualValue')}</th>
                            <th className="text-right">{t('priceSimulation.annualImpact')}</th>
                            <th className="text-right">
                                <WithTooltip content={t('priceSimulation.billedImpactTooltip')} focusable>
                                    <span>{t('priceSimulation.billedImpactColumn')}</span>
                                </WithTooltip>
                            </th>
                            <th className="text-right">{t('priceSimulation.weightedIncrease')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pkg.rows.map((row) => (
                            <ImpactTableRow key={row.key} row={row} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const AnalysisTabContent = ({ calculations }: AnalysisTabContentProps) => {
    const { t } = useTranslation();

    const packages = useMemo(() => calculateImpactBreakdown(calculations), [calculations]);

    return (
        <div className="d-flex flex-column gap-24 mt-24">
            {packages.map((pkg) => (
                <PackageImpactCard key={pkg.packageId} pkg={pkg} />
            ))}
            <div className="bg-default border rounded-8 overflow-hidden">
                <div className="d-flex justify-content-between align-items-center px-24 py-16">
                    <h3 className="heading-md m-0">{t('priceSimulation.total')}</h3>
                    <div className="d-flex flex-column align-items-end">
                        <WorksheetTotal
                            label={t('priceSimulation.annualValue')}
                            original={calculations.currentAnnualValue}
                            next={calculations.newAnnualValue}
                            hasQuantity={calculations.hasQuantity}
                            changeClassName="ml-4"
                        />
                        <BilledImpactLine amount={calculations.billedImpact} hasQuantity={calculations.hasQuantity} className="align-self-end" textClassName="body-sm" />
                    </div>
                </div>
            </div>
            {calculations.excludedTieredPlanCount > 0 && <ExcludedTieredPlansNote grossRevenue={calculations.excludedTieredGrossRevenue} />}
        </div>
    );
};

export default AnalysisTabContent;
